// src/pages/Home.js
import React, { useState, useEffect } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ApiClient from '../utils/ApiClient';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// Stat card component
const StatCard = ({ title, value, icon }) => (
    <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
            <div>
                <p className="text-sm font-medium text-gray-500">{title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
            </div>
            <div className="text-3xl text-gray-300">{icon}</div>
        </div>
    </div>
);

// Processed Returns Chart component
const ProcessedReturnsChart = () => {
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState('30');
    const [showCustom, setShowCustom] = useState(false);
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    const fetchData = async (days, startDate, endDate) => {
        setLoading(true);
        setError(null);
        try {
            let url = '/api/dashboard/processed-returns';
            if (startDate && endDate) {
                url += `?startDate=${startDate}&endDate=${endDate}`;
            } else {
                url += `?days=${days}`;
            }
            console.log('Fetching chart data from:', url);
            const response = await ApiClient.get(url);
            const data = response.data;
            console.log('Chart data response:', data);

            if (data.success) {
                setChartData({
                    labels: data.labels,
                    datasets: [
                        {
                            label: 'Units Processed',
                            data: data.data,
                            backgroundColor: 'rgba(59, 130, 246, 0.8)',
                            borderColor: 'rgba(59, 130, 246, 1)',
                            borderWidth: 1,
                            borderRadius: 4,
                        },
                    ],
                });
            } else {
                setError(data.message || 'Failed to fetch chart data');
            }
        } catch (err) {
            console.error('Chart fetch error:', err);
            setError(err.response?.data?.message || 'Failed to fetch chart data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedPeriod !== 'custom') {
            fetchData(selectedPeriod);
        }
    }, [selectedPeriod]);

    const handlePeriodChange = (e) => {
        const value = e.target.value;
        setSelectedPeriod(value);
        if (value === 'custom') {
            setShowCustom(true);
        } else {
            setShowCustom(false);
        }
    };

    const handleCustomDateSubmit = () => {
        console.log('Custom date submit - start:', customStartDate, 'end:', customEndDate);
        if (customStartDate && customEndDate) {
            fetchData(null, customStartDate, customEndDate);
        } else {
            console.warn('Missing dates - start:', customStartDate, 'end:', customEndDate);
        }
    };

    const getPeriodLabel = () => {
        if (selectedPeriod === 'custom' && customStartDate && customEndDate) {
            return `${customStartDate} to ${customEndDate}`;
        }
        return `Last ${selectedPeriod} Days`;
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                titleFont: {
                    size: 14,
                },
                bodyFont: {
                    size: 13,
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)',
                },
            },
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    maxRotation: 45,
                    minRotation: 45,
                },
            },
        },
    };

    const renderHeader = () => (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Processed Returns</h3>
            <div className="flex flex-wrap items-center gap-2">
                <select
                    value={selectedPeriod}
                    onChange={handlePeriodChange}
                    className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                >
                    <option value="30">Last 30 Days</option>
                    <option value="60">Last 60 Days</option>
                    <option value="90">Last 90 Days</option>
                    <option value="custom">Custom Range</option>
                </select>
                {showCustom && (
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                            className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                        />
                        <span className="text-gray-500">to</span>
                        <input
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                        />
                        <button
                            onClick={handleCustomDateSubmit}
                            disabled={!customStartDate || !customEndDate}
                            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                            Apply
                        </button>
                    </div>
                )}
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                {renderHeader()}
                <div className="h-64 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                {renderHeader()}
                <div className="h-64 flex items-center justify-center text-red-500">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow p-6">
            {renderHeader()}
            <div className="h-64">
                {chartData && <Bar data={chartData} options={options} />}
            </div>
        </div>
    );
};

const Home = () => {
    return (
        <div className="p-6 space-y-6">
            {/* Page Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                <p className="text-gray-500 mt-1">Overview of your Calibrate operations</p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard
                    title="Units In Amazon"
                    value="--"
                    icon="📦"
                />
                <StatCard
                    title="Warehouse Units"
                    value="--"
                    icon="🏠"
                />
                <StatCard
                    title="Pending Cases"
                    value="--"
                    icon="📋"
                />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 gap-6">
                <ProcessedReturnsChart />
            </div>
        </div>
    );
};

export default Home;
